import React, { ReactNode } from "react";
import styled from "styled-components";

// @ts-expect-error - JSXファイルの型定義なし
import TitleBlock from "../block/TitleBlock";
import {
  StepFlowBody,
  StepFlowContent,
  StepFlowFooter,
  StepFlowHeader,
} from "./StepFlowShell";
import type { StepFlowAction } from "./stepFlowActions";

/**
 * ページ（または画面全体を覆うオーバーレイ）として表示するステップフローのレイアウト。
 *
 * ```
 * ┌ 戻る ──────── [Stepper] ─────────────┐  ← StepFlowHeader
 * │           TitleBlock（title/description）│
 * │           children（最大 740px 中央寄せ）  │  ← StepFlowBody / StepFlowContent
 * ├────────────────────────────────────────┤
 * │      [戻る]            (children) [次へ] │  ← StepFlowFooter（同じ幅上限）
 * └────────────────────────────────────────┘
 * ```
 *
 * モーダルとして開くフロー（プロジェクト作成・求人応募など）は `Modal`
 * variant="fullscreen" + `bodyWidth="reading"` を使う。両者は同じ部品で
 * 描画されるので見え方は揃う。
 */
export interface StepFlowLayoutProps {
  /** 左端の戻るボタン */
  onBack?: () => void;
  backLabel?: string;
  /** ヘッダー中央のステップナビ（通常は `Stepper`） */
  stepper?: ReactNode;
  /** ヘッダー右端スロット */
  headerRight?: ReactNode;
  /** ステップナビの幅上限（既定 480px / "wide" は本文と同じ 740px） */
  stepperWidth?: "default" | "wide";
  /** ステップナビ直下に置くタイトルブロック */
  title?: string;
  description?: string;
  /** フッター左（戻る） */
  secondaryAction?: StepFlowAction;
  /** フッター右（次へ） */
  primaryAction?: StepFlowAction;
  /** フッター右ボタンの左隣スロット（合計金額など） */
  footerCenter?: ReactNode;
  /** 構造化アクションの代わりに自由配置する場合 */
  footer?: ReactNode;
  /** 本文をスクロール領域に固定せず、ページ全体でスクロールさせる場合に false */
  fixed?: boolean;
  /** アクセシビリティ用ラベル */
  ariaLabel?: string;
  children: ReactNode;
}

const Root = styled.div<{ $fixed: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  ${({ $fixed }) =>
    $fixed
      ? `
    position: fixed;
    inset: 0;
    height: 100dvh;
    /* GlobalHeader（6）・BottomAppBar（4）より上、Modal（1000）より下 */
    z-index: 10;
    overflow: hidden;
  `
      : `
    min-height: 100dvh;
  `}
`;

const StepFlowLayout: React.FC<StepFlowLayoutProps> = ({
  onBack,
  backLabel,
  stepper,
  headerRight,
  stepperWidth,
  title,
  description,
  secondaryAction,
  primaryAction,
  footerCenter,
  footer,
  fixed = true,
  ariaLabel,
  children,
}) => {
  const hasHeader = Boolean(onBack || stepper || headerRight);
  const hasFooter = Boolean(footer || primaryAction || secondaryAction || footerCenter);

  return (
    <Root $fixed={fixed} role={ariaLabel ? "region" : undefined} aria-label={ariaLabel}>
      {hasHeader && (
        <StepFlowHeader
          onBack={onBack}
          backLabel={backLabel}
          rightSlot={headerRight}
          stepperWidth={stepperWidth}
        >
          {stepper}
        </StepFlowHeader>
      )}
      <StepFlowBody>
        {/* u-items-stretch: フォーム等の子要素を読み幅いっぱいに広げる */}
        <StepFlowContent className="u-col u-items-stretch u-gap24">
          {title ? <TitleBlock title={title} description={description} /> : null}
          {children}
        </StepFlowContent>
      </StepFlowBody>
      {hasFooter && (
        <StepFlowFooter
          secondaryAction={secondaryAction}
          primaryAction={primaryAction}
          footer={footer}
        >
          {footerCenter}
        </StepFlowFooter>
      )}
    </Root>
  );
};

export default StepFlowLayout;
